import React from "react";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import useUndoneNotification from "../../components/useUndoneNotification";
import { useInputPopup } from "../../components/InputPopup";
import activityTypes from "@coworker/enums/activityTypes";
import taskStateOptions from "@coworker/enums/taskState";
import trackerHelper from "../../helpers/tracker";
import { Trans } from "@coworker/locales";
import { useUserId } from "../../core/auth/useLoggedInUser";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { useQueryClient } from "@tanstack/react-query";

export const useUndoPickTask = ({ task, afterUndo }) => {
  const undoCurtain = useUndoneNotification();
  const userId = useUserId();

  const undoTask = React.useCallback(
    async (actionId) => {
      const data = {
        last_editor_id: userId,
        assigned_user_id: userId,
        state: taskStateOptions.ASSIGNED,
        undo_action_id: actionId || "",
        pick_quantity: null,
        actual_pick_quantity: null,
        pick_type: "",
        picked: false,
        task_type_detail: "PICK_AND_REFILL",
        searchable_location:
          task.pickup_location_custom ?? task.pickup_location,
      };

      await undoCurtain(data, task.id, task.task_type);
      afterUndo();
    },
    [afterUndo, task, userId, undoCurtain]
  );

  return {
    call: undoTask,
  };
};

export const usePickTask = (task, taskId, config = {}) => {
  const { undo, afterUndo = () => {} } = config;
  const queryClient = useQueryClient();
  const userId = useUserId();
  const updateTaskMutation = useUpdateTaskMutation();
  const { showToastWithUndo } = useToastNotification();
  const { getInput } = useInputPopup();
  const { modalState, toggleModal } = React.useContext(ModalContext);
  const { call: undoTask } = useUndoPickTask({
    task: { ...task, id: taskId },
    afterUndo,
  });
  const pickTask = React.useCallback(async () => {
    const actionId = `picked-${new Date().getTime()}`;
    const handoff = await getInput("handoff", {});
    if (!handoff) return console.error("pickTask: handoff missing");
    let activityType = activityTypes.PICKED;

    if (
      handoff.gid !== task.assigned_team_id ||
      (!handoff.uid && handoff.gid === task.assigned_team_id)
    ) {
      activityType = activityTypes.PICKED_AND_REASSIGNED_TEAM;
    } else if (handoff.uid && handoff?.uid !== task.assigned_user_id) {
      activityType = activityTypes.PICKED_AND_REASSIGNED_USER;
    }

    const data = {
      last_editor_id: userId,
      assigned_user_id: handoff.uid || "",
      assigned_team_id: handoff.gid,
      state:
        handoff.uid === "" || handoff.uid === undefined
          ? taskStateOptions.UNASSIGNED
          : taskStateOptions.ASSIGNED,
      action_id: actionId,
      activity_type: activityType,
      // The onTaskUpdate trigger also deletes undo_action_id, but in the case that this backend change has not propagated to the frontend we must make sure to also signal this from the frontend!
      undo_action_id: "",
      picked: true,
      pick_quantity: task.requested_quantity,
      actual_pick_quantity: task.actual_requested_quantity,
      pick_type: task.requested_type,
      task_type_detail: "REFILL",
      searchable_location: task.location,
    };

    await updateTaskMutation.mutateAsync({ data, taskId });

    queryClient.removeQueries({
      queryKey: ["activityTasks", "inProgressTasks"],
    });

    if (data.assigned_user_id !== userId) {
      queryClient.removeQueries({
        queryKey: ["activityTasks", "openTasks"],
      });
    }

    if (modalState.isTaskDetailSheetOpen) {
      toggleModal({ type: ModalType.TaskDetail });
    }

    trackerHelper.trackPickAddonTask(
      { ...task, task_id: taskId, pick_quantity: task.requested_quantity },
      false
    );

    if (
      [
        activityTypes.PICKED_AND_REASSIGNED_TEAM,
        activityTypes.PICKED_AND_REASSIGNED_USER,
      ].includes(activityType)
    ) {
      trackerHelper.trackHandoffTask(taskId, handoff.uid, handoff.gid);
    }

    if (!undo) return;

    showToastWithUndo(
      handoff.uid === userId ? (
        <Trans>pickingNotificationString</Trans>
      ) : (
        <Trans>pickingNotification2String</Trans>
      ),
      () => undoTask(actionId)
    );

    return true;
  }, [
    undoTask,
    updateTaskMutation,
    undo,
    getInput,
    userId,
    showToastWithUndo,
    task,
    taskId,
    modalState.isTaskDetailSheetOpen,
    toggleModal,
    queryClient,
  ]);

  return {
    call: pickTask,
    updateTaskMutation: updateTaskMutation,
  };
};
